import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query'
import { StatusCodes } from 'http-status-codes'

type AppBaseQuery = BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
>

/**
 * Wraps a base query with 401 recovery: on UNAUTHORIZED, forces a token
 * refresh and retries the request once with the new token. Defense in depth
 * for KPS-377 — a request that races a token expiry must not permanently fail
 * the screen that issued it.
 */
export function createReauthBaseQuery(
  baseQuery: AppBaseQuery,
  refreshToken: () => Promise<boolean>,
): AppBaseQuery {
  return async (args, api, extraOptions) => {
    let result = await baseQuery(args, api, extraOptions)
    if (result.error?.status === StatusCodes.UNAUTHORIZED) {
      // Best-effort recovery: a failing refresh must surface the original 401,
      // not a thrown exception.
      const refreshed = await refreshToken().catch(() => false)
      if (refreshed) {
        result = await baseQuery(args, api, extraOptions)
      }
    }
    return result
  }
}
